// ZoneSharePage — top-level page rendered for /zones/:slug?token=<48-hex>.
// Bypasses the login screen entirely and renders the live ZoneBriefing
// against the share-token API surface. The api.ts interceptor injects the
// token automatically on every outbound API call (path-based).
//
// Chrome: footer confidentiality line only. The Atwater × Integra masthead
// inside ZoneBriefing carries the branding/recipient context.

import React, { useEffect, useState } from 'react';
import { ZoneBriefing } from './ZoneBriefing';
import { fetchZoneShareInfo } from '../../lib/api';
import { designTokens } from '../../styles/design-tokens';

interface Props {
  slug: string;
  token: string;
}

export const ZoneSharePage: React.FC<Props> = ({ slug, token: _token }) => {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Set <meta name="robots" content="noindex, nofollow"> so the share URL
    // doesn't get indexed if it ever leaks to a crawler.
    const existing = document.querySelector('meta[name="robots"]');
    const meta = existing as HTMLMetaElement | null ?? document.createElement('meta');
    meta.setAttribute('name', 'robots');
    meta.setAttribute('content', 'noindex, nofollow');
    if (!existing) document.head.appendChild(meta);
    return () => {
      // Leave the tag in place — it's harmless on subsequent navigations.
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    // Kick off the briefing fetch in parallel with share/info. ZoneBriefing
    // mounts only after share/info resolves, so without this prefetch the
    // ~1s briefing round-trip is serial after the share/info round-trip.
    // Browser HTTP-caches the response; ZoneBriefing's later api.get hits
    // the cache instantly. Errors swallowed — ZoneBriefing handles its own
    // fetch + error state if this prefetch fails.
    fetch(`/api/zones/${encodeURIComponent(slug)}/briefing?token=${encodeURIComponent(_token)}`, {
      signal: controller.signal,
      headers: { 'Accept-Encoding': 'gzip' },
    }).catch(() => {});
    fetchZoneShareInfo(controller.signal)
      .then(() => {})
      .catch((e) => {
        if (e?.name !== 'CanceledError') {
          // 401 / 403 / 410 — the token is invalid, expired, or revoked.
          // Render the error state.
          const status = e?.response?.status;
          if (status === 401 || status === 403) setError('This share link is invalid or has been revoked.');
          else if (status === 410) setError('This share link has expired.');
          else setError('Could not load share view.');
        }
      });
    return () => controller.abort();
  }, [slug, _token]);

  if (error) {
    return (
      <div style={errorPage}>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <div style={{ fontSize: 11, letterSpacing: 2, color: designTokens.colors.brand, marginBottom: 12 }}>ATWATER ENERGY</div>
          <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#1a2533' }}>Link unavailable</h1>
          <p style={{ fontSize: 13, color: '#5a6878', lineHeight: 1.5 }}>{error}</p>
          <p style={{ fontSize: 12, color: '#8a98a8', marginTop: 16 }}>If you were expecting access, contact the person who shared this with you.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="zone-share-wrapper" style={pageWrap}>
      <main style={main}>
        <ZoneBriefing slug={slug} shareToken={_token} />
      </main>

      <footer style={chromeFooter}>
        Shared by Atwater Energy Co. — Do not redistribute.
      </footer>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────

const pageWrap: React.CSSProperties = {
  minHeight: '100vh',
  background: '#f5f3ee',
  display: 'flex',
  flexDirection: 'column',
};

const main: React.CSSProperties = {
  flex: 1,
};

const chromeFooter: React.CSSProperties = {
  padding: '14px 24px',
  textAlign: 'center',
  background: '#1a2533',
  color: 'rgba(245,243,238,0.45)',
  fontFamily: designTokens.typography.fontFamily.display,
  fontSize: 11,
  letterSpacing: 0.4,
  borderTop: `1px solid rgba(196,112,90,0.3)`,
};

const errorPage: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f5f3ee',
  fontFamily: designTokens.typography.fontFamily.display,
  padding: 32,
};
